import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { tryToFetchInvoices, tryToSendEmailInvoice } from '../../../store/brokers/suppliers/invoices/InvoicesSlice';
import { tryToFetchAllPartners } from '../../../store/brokers/admin/suppliers/SuppliersSlice';
import { IPaginationPayload } from '../../../interfaces/shared/IPaginationPayload';
import { IToast } from '../../../interfaces/components/IToast';
import InvoiceDetailsModal from './modals/InvoiceDetailsModal';
import EditDetailsModal from './modals/EditDetailsModal';
import InvoiceFilters from './details/InvoiceFilters';
import InvoicesTable from './tables/InvoicesTable';
import Button from '../../../shared/button';
import Input from '../../../shared/input';
import Toast from '../../../shared/toast';


const Invoices = () => {
    const dispatch = useAppDispatch();
    const [search, setSearch] = useState<string>();
    const [rows, setRows] = useState<Array<any>>([]);
    const [partners, setPartners] = useState<any>();
    const [selectedPartner, setSelectedPartner] = useState<any>();
    const [selectedStatus, setSelectedStatus] = useState<any>();
    const [invoiceId, setInvoiceId] = useState<string>();
    const [showToast, setShowToast] = useState<IToast | null>();
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const paginationData = useAppSelector((state) => state?.invoices?.invoices?.data?.page);
    const rowsLoading = useAppSelector((state) => state.invoices.invoicesAreLoading);
    const state = useAppSelector((state) => state?.invoices)

    const onResetPaginationState = () => {
        setPaginationState({
            pageNumber: 1,
            pageSize: paginationState?.pageSize || 10
        })
    }

    const getPartnersData = async () => {
        try {
            const partnerResponse: any = await dispatch(tryToFetchAllPartners(null)).unwrap()
            const formatPartners = partnerResponse && partnerResponse?.data && partnerResponse?.data?.length > 0 && partnerResponse?.data?.map((partner: any) => ({ ...partner, label: partner?.companyName, value: partner?._id, name: partner?.name }))
            setPartners(formatPartners || [])
        } catch (err) {
            // error here
        }
    }

    const onGettingFilters = () => {
        let data: any
        if (search) {
            data = {
                ...data || {},
                search: search,
            }
        }
        if (selectedStatus?.value) {
            const value = selectedStatus?.value === 'Yes'
            data = {
                ...data || {},
                invoiced: value
            }
        }
        if (selectedPartner) {
            data = {
                ...data || {},
                partnerId: selectedPartner?.value,
            }
        }
        const formatData: any = { data: data }
        return formatData;
    }

    const onGettingInvoiceData = async () => {
        try {
            const data = onGettingFilters()
            const fetchData = {
                ...paginationState || {},
                ...data || {}
            }
            await dispatch(tryToFetchInvoices(fetchData)).unwrap();
        } catch (err) {
            // error
        }
    }

    const onChangeFilterSelected = (value: any, type?: string) => {
        if (type) {
            switch (type) {
                case 'status':
                    setSelectedStatus(value)
                    break;
                case 'partner':
                    setSelectedPartner(value)
                    break;
            }
            onResetPaginationState()
        }
    }

    useEffect(() => {
        if (state.invoices) {
            const rows = state.invoices?.data?.elements
            setRows(rows);
        }
    }, [state.invoices]);

    useEffect(() => {
        onGettingInvoiceData()
        getPartnersData()
    }, [])

    useEffect(() => {
        onGettingInvoiceData()
    }, [paginationState])

    const onClickClearAllFilter = () => {
        setSearch(undefined);
        setSelectedPartner(undefined)
        setSelectedStatus(undefined)
        onResetPaginationState();
    }

    const onChangeSearchValue = (value: string | null | undefined, type: string) => {
        setSearch(value || undefined)
        onResetPaginationState()
    }

    const onSeeInvoiceDetails = (id: string) => {
        setInvoiceId(id)
        setShowDetailsModal(true)
        document.body.style.overflow = 'hidden';
    }

    const onDownloadInvoice = (pdfLink: string) => {
        window.open(pdfLink, '_blank')
    }

    const onShowEditInvoiceDetails = (id: string) => {
        setInvoiceId(id)
        setShowEditModal(true)
        document.body.style.overflow = 'hidden';
    }

    const onHideDetailsModal = () => {
        setInvoiceId(undefined)
        setShowEditModal(false)
        setShowDetailsModal(false)
        document.body.style.overflow = 'auto';
    }

    const onSendInvoice = async (id: string) => {
        try {
            await dispatch(tryToSendEmailInvoice(id)).unwrap()
            setShowToast({ type: 'success', message: 'Successfully invoice email' })
        } catch (err) {
            setShowToast({ type: 'error', message: `${err}` })
        }
    }

    return (
        <div>
            <div>
                <p className='page-title'>Invoices</p>
            </div>
            <div>
                <InvoiceFilters
                    selectedPartner={selectedPartner}
                    selectedStatus={selectedStatus}
                    partners={partners}
                    onClickFilter={onChangeFilterSelected}
                />
            </div>
            <div className={`flex flex-row justify-between items-center mt-3 bg-[#f4f4f4] mb-2 p-1 !rounded`}>
                <div>
                    <Input
                        placeholder={'Filter Table'}
                        dataQa={'search'}
                        containerStyle='mb-0 pb-0 relative min-w-[500px]'
                        inputStyle={`mb-0 border-0 focus:outline-none`}
                        icon={'/assets/shared/search.svg'}
                        showValue={true}
                        inputValue={search}
                        inputUniqueName={'search'}
                        onChangeInput={onChangeSearchValue}
                        hideInputSpace={true}
                    />
                </div>
                <div className='flex flex-row justify-end'>
                    <div className='flex flex-row justify-end ml-2'>
                        <Button
                            label='Clear Filters'
                            className={'btn-primary-text-underline min-w-auto'}
                            onClickButton={() => onClickClearAllFilter()}
                        />
                    </div>
                </div>
            </div>
            <div>
                <InvoicesTable
                    rows={rows}
                    rowsLoading={rowsLoading}
                    paginationData={paginationData}
                    setPaginationState={setPaginationState}
                    onSeeDetails={onSeeInvoiceDetails}
                    onDownloadInvoice={onDownloadInvoice}
                    onEditDetails={onShowEditInvoiceDetails}
                    onSendInvoice={onSendInvoice}
                />
            </div>
            {showToast?.message &&
                <Toast
                    type={showToast?.type}
                    text={showToast?.message}
                    onHandleCloseToast={() => setShowToast(null)}
                />
            }
            {showDetailsModal &&
                <InvoiceDetailsModal
                    openModal={showDetailsModal}
                    invoiceId={invoiceId || ''}
                    handleCloseModal={onHideDetailsModal}
                />
            }
            {showEditModal &&
                <EditDetailsModal
                    openModal={showEditModal}
                    invoiceId={invoiceId || ''}
                    handleCloseModal={onHideDetailsModal}
                />
            }
        </div>
    )
}
export default Invoices;